import { QuestionMarkCircleIcon } from '@heroicons/react/24/solid';
import React, { FunctionComponent, useState } from 'react';
import { BriefKeyword } from '../../services/models/brief-keyword';
import HelpDialog from '../help-dialog/HelpDialog';
import { TagInput } from '../ui';
import { getExactMatchTagClassName } from '../ui/TagInput';

export interface EditBriefKeywordProps {
  title: string;
  maxItems?: number;
  helpSummary: JSX.Element;
  helpContent: JSX.Element;
  keywords: BriefKeyword[];
  optional?: boolean;
  placeholder?: string;
  isUpdating: boolean;
  create: (keyword: string) => void;
  destroy: (keywordId: number) => void;
  validate?: (keyword: string) => null | string;
}

/**
 * Domain wrapper that edits a brief's persisted keywords through TagInput:
 * chip changes are diffed into per-keyword create/destroy mutations, and the
 * chips re-render from the refetched brief.
 */
const EditBriefKeyword: FunctionComponent<EditBriefKeywordProps> = ({
  title,
  placeholder,
  maxItems,
  helpSummary,
  helpContent,
  keywords,
  isUpdating,
  optional,
  create,
  destroy,
  validate,
}) => {
  const [error, setError] = useState('');
  const [showHelpDialog, setShowHelpDialog] = useState(false);

  const handleCloseHelpDialog = () => setShowHelpDialog(false);
  const handleShowHelpDialog = () => setShowHelpDialog(true);

  const values = (keywords || []).map(x => x.keyword || '');

  const canDeleteKeywords = optional || values.length > 1;

  const handleAdd = (next: string[]) => {
    // Reject additions while a save is in flight: returning false makes
    // TagInput keep the draft, so the typed text survives and commits once
    // the mutation lands. Vetoing this inside handleChange instead would
    // clear the draft — silent keyword loss (#327).
    if (isUpdating) {
      return false;
    }

    if (next.length > (maxItems || 20)) {
      return false;
    }

    if (validate) {
      for (const keyword of next.slice(values.length)) {
        const result = validate(keyword);

        if (typeof result === 'string' && result.length > 0) {
          setError(result);
          return false;
        }
      }
    }

    setError('');
    return true;
  };

  const handleChange = (next: string[]) => {
    // In-flight veto, belt-and-braces with handleAdd: `values` derives from
    // the server-backed `keywords` prop, so applying a change mid-flight
    // would diff against a stale snapshot. Additions are already rejected in
    // handleAdd (preserving the draft); removals bypass onAdd and land here,
    // where vetoing is lossless — the chip simply stays because `keywords`
    // is unchanged until the server confirms.
    if (isUpdating) {
      return;
    }

    next.filter(x => !values.includes(x)).forEach(x => create(x));

    (keywords || [])
      .filter(x => x.id !== undefined && !next.includes(x.keyword || ''))
      // Removal of the last required keyword is also vetoed.
      .forEach(x => {
        if (canDeleteKeywords) {
          destroy(x.id as number);
        }
      });
  };

  return (
    <div className="EditBriefKeyword mb-4">
      <div className="mb-1">
        <span className="font-['Ubuntu'] text-[18px] font-bold tracking-[0.5px] text-primary">
          {title}
        </span>
      </div>
      <p className="mt-[5px] mb-[10px] block text-[13px] text-gray-500">
        <span className="text-gray-400">{helpSummary}</span>
        <QuestionMarkCircleIcon
          className="inline-block h-4 w-4 ml-1 align-text-bottom text-gray-400 cursor-pointer"
          aria-hidden="true"
          onClick={handleShowHelpDialog}
        />
      </p>
      <TagInput
        values={values}
        placeholder={placeholder || 'Add keyword'}
        maxLength={52}
        onAdd={handleAdd}
        onChange={handleChange}
        getTagClassName={tag => getExactMatchTagClassName(keywords, tag)}
      />
      <div className="pt-1 text-gray-400">
        [{values.length}/{maxItems}] keywords / phrase
      </div>
      {error.length > 0 && <p className="mt-1 text-sm text-red-600">{error}</p>}

      <HelpDialog show={showHelpDialog} onHide={handleCloseHelpDialog}>
        {helpContent}
      </HelpDialog>
    </div>
  );
};

export default EditBriefKeyword;
